"use client";
/**
 * Client crash reporter.
 *
 * The server has tripwires for every way it can stall ([loop-stall],
 * [slow-sql], [stall-profile]). The browser had none: a pane that threw, the
 * router's "Something went wrong!" screen, and an unhandled rejection all
 * stayed in one window's console, so "the app crashed" could not be traced to
 * anything. This sends each distinct crash to POST /api/client-log, which
 * writes one `[client-error]` line to the journal.
 *
 * Bounded on purpose, because a crash loop must not become a request loop:
 *   - one report per signature (message + top of stack) per CRASH_DEDUPE_MS
 *   - at most CRASH_MAX_PER_LOAD reports per page load
 *   - message, stack and component stack are truncated, and the JSON body is
 *     shrunk until it fits CRASH_MAX_BODY_BYTES
 *
 * Plain fetch, NOT sendBeacon or `keepalive`. Those share one 64 KiB in-flight
 * budget per document with prefs sync's unload flush (README: "Saved but gone
 * after reload"). A burst of crash reports on that budget could crowd out the
 * one request that saves the user's layout. Every error reported here leaves
 * the page alive, so an ordinary request completes.
 *
 * The pure core (`createCrashReporter`, `buildCrashReport`) touches no DOM so
 * `npm run clientcrash:check` can pin the limits offline.
 */

export const CRASH_REPORT_URL = "/api/client-log";
export const CRASH_DEDUPE_MS = 5 * 60_000;
export const CRASH_MAX_PER_LOAD = 20;
export const CRASH_MAX_MESSAGE = 500;
export const CRASH_MAX_STACK = 4096;
export const CRASH_MAX_COMPONENT_STACK = 1024;
export const CRASH_MAX_BODY_BYTES = 8192;

export type CrashKind = "error" | "rejection" | "route" | "pane" | "uncaught";

export type CrashReport = {
  kind: CrashKind;
  message: string;
  stack: string;
  componentStack: string;
  route: string;
  build: string;
  userAgent: string;
  ts: number;
};

export type CrashContext = { route: string; build: string; userAgent: string };

function clip(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Message and stack from whatever was thrown. Strings, objects and nulls are
 *  thrown too, and a rejection reason is often not an Error at all. */
export function errorParts(value: unknown): { message: string; stack: string } {
  if (value instanceof Error) {
    const name = value.name && value.name !== "Error" ? `${value.name}: ` : "";
    return { message: `${name}${value.message || "(no message)"}`, stack: value.stack ?? "" };
  }
  if (typeof value === "string") return { message: value, stack: "" };
  try {
    return { message: JSON.stringify(value) ?? String(value), stack: "" };
  } catch {
    return { message: String(value), stack: "" };
  }
}

/** First frame that is code, not the "Error: message" header. V8
 *  ("    at fn (url:1:2)") and Firefox/Safari ("fn@url:1:2"). */
export function topStackFrame(stack: string): string {
  for (const line of stack.split("\n")) {
    const t = line.trim();
    if (t.startsWith("at ") || /@\S+:\d+/.test(t)) return t;
  }
  return "";
}

/** Same crash = same message thrown from the same site. Only the TOP frame:
 *  the callers below it differ when one bug is reached from several places
 *  (measured in the browser: one throw site through three timers gave three
 *  different six-frame stacks and three reports). Line/column stay in, so two
 *  throw sites with one message are still two reports. */
export function crashSignature(message: string, stack: string): string {
  return `${message}\n${topStackFrame(stack)}`;
}

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** Truncate, then shrink the stacks until the JSON body fits the cap. */
export function buildCrashReport(
  kind: CrashKind,
  error: unknown,
  ctx: CrashContext,
  ts: number,
  componentStack = "",
): { report: CrashReport; body: string } {
  const parts = errorParts(error);
  const report: CrashReport = {
    kind,
    message: clip(parts.message, CRASH_MAX_MESSAGE),
    stack: clip(parts.stack, CRASH_MAX_STACK),
    componentStack: clip(componentStack, CRASH_MAX_COMPONENT_STACK),
    route: clip(ctx.route, 200),
    build: clip(ctx.build, 64),
    userAgent: clip(ctx.userAgent, 300),
    ts,
  };
  let body = JSON.stringify(report);
  // Multi-byte text can outgrow the cap even after the character clips.
  while (byteLength(body) > CRASH_MAX_BODY_BYTES) {
    if (report.componentStack) report.componentStack = "";
    else if (report.stack.length > 64) report.stack = report.stack.slice(0, report.stack.length >> 1);
    else report.message = report.message.slice(0, report.message.length >> 1);
    body = JSON.stringify(report);
  }
  return { report, body };
}

export type CrashReporter = {
  /** True when the report was sent, false when deduped, capped or failed. */
  report: (kind: CrashKind, error: unknown, extra?: { componentStack?: string | null }) => boolean;
  sent: () => number;
};

export function createCrashReporter(opts: {
  send: (body: string) => void;
  context: () => CrashContext;
  now?: () => number;
}): CrashReporter {
  const now = opts.now ?? Date.now;
  const lastSent = new Map<string, number>();
  let count = 0;
  return {
    report(kind, error, extra) {
      try {
        if (count >= CRASH_MAX_PER_LOAD) return false;
        const t = now();
        const { report, body } = buildCrashReport(
          kind,
          error,
          opts.context(),
          t,
          extra?.componentStack ?? "",
        );
        const sig = crashSignature(report.message, report.stack);
        const prev = lastSent.get(sig);
        if (prev != null && t - prev < CRASH_DEDUPE_MS) return false;
        lastSent.set(sig, t);
        count += 1;
        opts.send(body);
        return true;
      } catch {
        // The reporter must never be the next crash.
        return false;
      }
    },
    sent: () => count,
  };
}

/** Errors the browser raises that are not application faults. */
export function isCrashNoise(message: string, name?: string): boolean {
  if (name === "AbortError") return true;
  return (
    message.startsWith("ResizeObserver loop") ||
    message === "Script error." ||
    message.includes("The user aborted a request")
  );
}

/** Which kind a React error boundary was. PaneErrorBoundary tags itself; the
 *  only other boundaries in the tree are TanStack's per-route CatchBoundary. */
export function boundaryKind(boundary: unknown): CrashKind {
  const ctor = (boundary as { constructor?: { reportKind?: unknown } } | null | undefined)?.constructor;
  return ctor?.reportKind === "pane" ? "pane" : "route";
}

/** `index-<hash>` of the entry chunk, so a report names the build it came
 *  from. "dev" under the Vite dev server. */
function currentBuild(): string {
  const src =
    document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.src ?? "";
  return src.match(/(index-[A-Za-z0-9_-]+)\.js/)?.[1] ?? "dev";
}

let installed: CrashReporter | null = null;
let suppressed = false;

/** Stop reporting for the rest of this page's life. The stale-chunk handler
 *  calls it before reloading: the failed import it swallowed can make React
 *  throw on the way out, and that is not a crash worth a log line. */
export function suppressCrashReports(): void {
  suppressed = true;
}

/** Browser-only. Idempotent. Returns the reporter the React root hooks use. */
export function installCrashReporter(): CrashReporter {
  if (installed) return installed;
  const build = currentBuild();
  const reporter = createCrashReporter({
    send(body) {
      if (suppressed) return;
      void fetch(CRASH_REPORT_URL, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body,
      }).catch(() => {});
    },
    context: () => ({
      route: window.location.pathname,
      build,
      userAgent: navigator.userAgent,
    }),
  });
  window.addEventListener("error", (event) => {
    if (!(event instanceof ErrorEvent)) return;
    const err = event.error as { name?: string } | undefined;
    if (isCrashNoise(event.message ?? "", err?.name)) return;
    reporter.report("error", event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { name?: string; message?: string } | undefined;
    if (isCrashNoise(String(reason?.message ?? reason ?? ""), reason?.name)) return;
    reporter.report("rejection", event.reason);
  });
  installed = reporter;
  return reporter;
}
